import React from 'react';
import { useI18n } from '../hooks/useI18n';
import { ServiceItem } from '../types/cabinet.types';

// Import icons as React components
import OverweightIcon from '../../assets/image/overweight.svg?react';
import CheckIcon from '../../assets/image/check.svg?react';
import FileTextIcon from '../../assets/image/file-text.svg?react';
import PhoneCallIcon from '../../assets/image/phone-call.svg?react';
import FilePlusIcon from '../../assets/image/file-plus.svg?react';
import HeartHandshakeIcon from '../../assets/image/heart-handshake.svg?react';
import TubeIcon from '../../assets/image/analysis.svg?react';
import ziolo24Logo from '../../assets/image/ziolo24-logo.png';

// Arrow Icon for the right side
const ChevronRightIcon = () => (
  <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 13L7 7L1 1" stroke="#362C4E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);



const ServiceCardBC: React.FC<{ service: ServiceItem; onClick: () => void }> = ({ service, onClick }) => {
  const isZiolo = service.id === 'ziolo24';

  return (
    <div className={`cab-svc-card-bc ${isZiolo ? 'cab-svc-card-bc--ziolo' : ''}`} onClick={onClick}>
      <div className="cab-svc-ic-bc">
        {service.icon}
      </div>
      <div className="cab-svc-content-bc">
        <h4>{service.title}</h4>
        <p dangerouslySetInnerHTML={{ __html: service.description }}></p>
      </div>
      <div className="cab-svc-arrow-bc">
        <ChevronRightIcon />
      </div>
      <style>{`
        .cab-page-title-bc {
          text-align: center;
          margin-bottom: 34px!important;
          color: #2F116A;
          font-size: 22px;
          font-weight: 800;
          line-height: 1.2;
        }
        .cab-svc-card-bc {
          display: flex;
          align-items: center;
          gap: 16px;
          cursor: pointer;
          background: #FFFFFF;
          border-radius: 16px;
          padding: 14px 16px;
          box-shadow: 0px 2px 10px rgba(54, 23, 119, 0.04);
          border: 1px solid rgba(54, 23, 119, 0.02);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .cab-svc-card-bc:hover {
          transform: translateY(-2px);
          box-shadow: 0px 4px 12px rgba(54, 23, 119, 0.05);
        }
        .cab-svc-ic-bc {
          flex-shrink: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
        }
        .cab-svc-ic-bc svg {
          width: 24px;
          height: 24px;
          object-fit: contain;
        }
        .cab-svc-ic-bc img {
          width: 44px;
          height: auto;
          max-height: 24px;
          object-fit: contain;
        }
        .cab-svc-content-bc {
          flex-grow: 1;
        }
        .cab-svc-content-bc h4 {
          font-size: 16px;
          color: #2F116A;
          margin: 0 0 4px 0;
          font-weight: 700;
          line-height: 1.2;
        }
        .cab-svc-content-bc p {
          font-size: 13px;
          color: #2F116A;
          line-height: 1.4;
          margin: 0;
          font-weight: 400;
        }
        .cab-svc-content-bc p strong {
          font-weight: 700;
          color: #2F116A;
        }
        .cab-svc-arrow-bc {
          display: none;
        }

        /* --- Ziolo24 Cross-sell specific card styling on Mobile (гармонійно з іншими: лого зліва) --- */
        .cab-svc-card-bc--ziolo {
          gap: 10px;
        }
        .cab-svc-card-bc--ziolo .cab-svc-ic-bc {
          width: 44px;
          height: 32px;
        }
        .cab-svc-card-bc--ziolo .cab-svc-ic-bc img {
          width: 44px;
          height: auto;
          max-height: 24px;
          object-fit: contain;
        }

        @media (min-width: 1024px) {
          .cab-page-title-bc {
            text-align: left;
            margin-bottom: 54px!important;
          }
          .cab-svc-card-bc {
            padding: 12px;
            gap: 20px;
            border-radius: 20px;
            box-shadow: none;
          }
          .cab-svc-ic-bc {
            width: 80px;
            height: 80px;
            background: #F8F6FB;
            border-radius: 20px;
          }
          .cab-svc-ic-bc img {
            width: 54px;
            height: auto;
            max-height: 36px;
          }
          .cab-svc-arrow-bc {
            display: flex !important;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            margin-left: 8px;
            margin-right: 8px;
          }
          .cab-svc-content-bc h4 {
            font-size: 16px;
          }
          .cab-svc-content-bc p {
            font-size: 13px;
          }

          /* Desktop styling for Ziolo24 — в одному стилі, стрілка як в інших */
          .cab-svc-card-bc--ziolo {
            gap: 20px !important;
          }
          .cab-svc-card-bc--ziolo .cab-svc-ic-bc {
            width: 80px !important;
            height: 80px !important;
          }
          .cab-svc-card-bc--ziolo .cab-svc-ic-bc img {
            width: 54px !important;
            height: auto !important;
            max-height: 36px !important;
          }
          .cab-svc-card-bc--ziolo .cab-svc-arrow-bc {
            display: flex !important;
          }
        }
      `}</style>
    </div>
  );
};

export const ServicesPageBC: React.FC = () => {
  const { t, lang } = useI18n();
  const currentLang = lang || 'pl';

  const services: ServiceItem[] = [
    {
      id: 'glp1',
      title: t('glp_t'),
      description: t('glp_d'),
      icon: <OverweightIcon />,
      isGlp1: true
    },
    {
      id: 'check',
      title: t('cs_check'),
      description: t('cs_check_d'),
      icon: <TubeIcon />,
      isGlp1: false
    },
    {
      id: 'recepta',
      title: t('cs_recepta'),
      description: t('cs_recepta_d'),
      icon: <FileTextIcon />,
      isGlp1: false
    },
    {
      id: 'tele',
      title: t('cs_tele'),
      description: t('cs_tele_d'),
      icon: <PhoneCallIcon />,
      isGlp1: false
    },
    {
      id: 'anty',
      title: t('cs_anty'),
      description: t('cs_anty_d'),
      icon: <FilePlusIcon />,
      isGlp1: false
    },
    {
      id: 'l4',
      title: t('cs_l4'),
      description: t('cs_l4_d'),
      icon: <HeartHandshakeIcon />,
      isGlp1: false
    },
    ...(currentLang === 'pl' ? [{
      id: 'ziolo24',
      title: 'Terapia konopiami medycznymi',
      description: 'Wizyta stacjonarna w Ziolo24 w całej Polsce — pierwsza wizyta <strong>od 149 zł</strong>. <strong>−10% z kodem TABLETKA24</strong>',
      icon: <img src={ziolo24Logo} alt="Ziolo24" />,
      isGlp1: false
    }] : [])
  ];

  const handleServiceClick = (id: string) => {
      if (id === 'ziolo24') {
          window.location.href = 'https://ziolo24.pl/?utm_source=tabletka24&utm_medium=konto&utm_campaign=crosssell';
          return;
      }
      if (typeof window !== 'undefined' && window.cabinetVars?.siteUrl) {
          // Use current React language instead of initial page load language
          const currentLang = lang || 'pl';
          
          const slugMap: Record<string, Record<string, string>> = {
              'pl': {
                  'glp1': '/usluga/leczenie-otylosci-online/',
                  'recepta': '/usluga/e-konsultacja/',
                  'anty': '/usluga/antykoncepcja/',
                  'l4': '/usluga/l4/',
                  'tele': '/usluga/teleporada-direct/',
                  'check': '/usluga/lab-pakiet-ogolny/'
              },
              'uk': {
                  'glp1': '/uk/usluga/likuvannya-zajvoyi-vagy/',
                  'recepta': '/uk/usluga/elektronnyj-reczept/',
                  'anty': '/uk/usluga/e-reczept-na-kontraczepcziyu-onlajn/',
                  'l4': '/uk/usluga/elektronna-l4/',
                  'tele': '/uk/usluga/teleporada-osnovna/',
                  'check': '/uk/usluga/lab-zagalnyj-paket/'
              },
              'ru': {
                  'glp1': '/ru/usluga/lechenie-lishnego-vesa/',
                  'recepta': '/ru/usluga/elektronnyj-reczept-ru/',
                  'anty': '/ru/usluga/kontraczeptivy-po-elektronnomu-reczeptu/',
                  'l4': '/ru/usluga/elektronnaya-l4/',
                  'tele': '/ru/usluga/telefonnaya-konsultacziya-osnovnaya/',
                  'check': '/uk/usluga/lab-zagalnyj-paket/'
              }
          };
          
          const slug = slugMap[currentLang]?.[id] || slugMap['pl'][id] || '/';
          window.location.href = `${window.cabinetVars.siteUrl}${slug}`;
      }
  };

  return (
    <div style={{ paddingTop: '20px', paddingBottom: '40px' }}>
      <h1 className="cab-page-title-bc">
        {t('nav_umow')}
      </h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {services.map(service => (
          <ServiceCardBC 
            key={service.id} 
            service={service} 
            onClick={() => handleServiceClick(service.id)} 
          />
        ))}
      </div>
    </div>
  );
};
